import { createContext, useCallback, useContext, useMemo, useState } from "react";

/**
 * FavoritesContext — holds the current session's favorite listing IDs.
 *
 * MVP scope note: this provider currently manages favorites as in-memory
 * client state. Persisting favorites to Firestore per authenticated user
 * is implemented alongside the Property Listing System (Phase 6) — the
 * `toggleFavorite` API below is designed so that swapping the local
 * implementation for a Firestore-backed one will not require any consuming
 * component to change.
 */

const FavoritesContext = createContext(undefined);

export function FavoritesProvider({ children }) {
  const [favoriteIds, setFavoriteIds] = useState([]);

  const toggleFavorite = useCallback((listingId) => {
    setFavoriteIds((current) =>
      current.includes(listingId)
        ? current.filter((id) => id !== listingId)
        : [...current, listingId]
    );
  }, []);

  const isFavorite = useCallback(
    (listingId) => favoriteIds.includes(listingId),
    [favoriteIds]
  );

  const value = useMemo(
    () => ({ favoriteIds, toggleFavorite, isFavorite }),
    [favoriteIds, toggleFavorite, isFavorite]
  );

  return (
    <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error("useFavorites must be used within a FavoritesProvider");
  }
  return context;
}
